import React, { useEffect } from "react";

import {
  CreditCardIcon,
  InvoiceIcon,
  SepaIcon,
  Typography
} from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { Button } from "ds/Button";
import Dialog from "ds/Dialog";
import Tag from "ds/Tag";
import { organizationPaymentSourceSelector } from "Reducers/organization/paymentSource/paymentSource.selectors";
import { getOrganizationPrepayment } from "Reducers/organization/prepayment";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "../Panel/Panel.style";

import type { Organization } from "@packages/client";

type PaymentMethodPanelProps = {
  organization?: Organization;
};

const PaymentMethodPanel = ({ organization }: PaymentMethodPanelProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const prepayment = useAppSelector(state => {
    if (organization) {
      return state.organizationPrepayment.data[organization.id];
    }
  });

  const paymentSource = useAppSelector(state => {
    if (organization) {
      return organizationPaymentSourceSelector(state, {
        organizationId: organization.name
      });
    }
  });

  useEffect(() => {
    if (organization) {
      dispatch(getOrganizationPrepayment({ organizationId: organization.id }));
    }
  }, [dispatch, organization]);

  if (!organization) {
    return null;
  }

  const paymentDetails: {
    icon: React.ReactNode;
    left?: string | null;
    right?: string | null;
  } = {
    icon: null,
    left: null,
    right: null
  };

  switch (paymentSource?.type) {
    case "prepaid": {
      paymentDetails.left = intl.formatMessage({
        id: "billing.prepaid_funds_remaining"
      });
      paymentDetails.right = prepayment
        ? intl.formatMessage(
            { id: "updated_moment" },
            { moment: moment(prepayment.last_updated_at).format("MMM D YYYY") }
          )
        : null;
      break;
    }
    case "credit-card": {
      paymentDetails.icon = <CreditCardIcon />;
      paymentDetails.left = paymentSource.number;
      paymentDetails.right = `${intl.formatMessage({ id: "payment_elements.expiration" })}: ${paymentSource.data?.exp_month}/${paymentSource.data?.exp_year?.slice(2)}`;
      break;
    }
    case "stripe_sepa_debit": {
      paymentDetails.icon = <SepaIcon />;
      paymentDetails.left = paymentSource.number;
      paymentDetails.right = `${intl.formatMessage({ id: "payment_elements.mandate" })}: ${paymentSource.data?.mandate_reference}`;
      break;
    }
    case "invoice_account": {
      paymentDetails.icon = <InvoiceIcon />;
      paymentDetails.left = "Paid by invoice";
      paymentDetails.right = null;
      break;
    }
    default: {
      paymentDetails.left = intl.formatMessage({
        id: "billing.no_payment_details"
      });
      break;
    }
  }

  const InsufficientPayment = () => {
    const Fallback = () => {
      switch (prepayment?.fallback) {
        case "invoice":
          return (
            <>
              <Typography.BodyText
                style={{
                  color: "var(--mode-text-neutral-weakest)"
                }}
                size="extraExtraSmall"
              >
                {intl.formatMessage({ id: "billing.backup_payment_method" })}:
              </Typography.BodyText>
              <O.SpacedRow>
                <InvoiceIcon />
                <S.SmallText>
                  {intl.formatMessage({ id: "billing.paid_by_invoice" })}
                </S.SmallText>
              </O.SpacedRow>
            </>
          );
        case "credit-card":
          return (
            <>
              <Typography.BodyText
                style={{
                  color: "var(--mode-text-neutral-weakest)"
                }}
                size="extraExtraSmall"
              >
                {intl.formatMessage({ id: "billing.backup_payment_method" })}:
              </Typography.BodyText>
              <O.SpacedRow align="start">
                <CreditCardIcon />
                <S.PanelRow>
                  <S.SmallText>
                    {paymentSource?.number}
                    <br />
                    {`${intl.formatMessage({ id: "payment_elements.expiration" })}: ${paymentSource?.data?.exp_month}/${paymentSource?.data?.exp_year?.slice(2)}`}
                  </S.SmallText>
                </S.PanelRow>
              </O.SpacedRow>
            </>
          );
        default:
          return (
            <p>
              <Link to={`/${organization.name}/-/billing/details`}>
                {intl.formatMessage({ id: "billing.add_a_payment_method" })}
              </Link>
            </p>
          );
      }
    };

    return prepayment && !prepayment.sufficient ? (
      <Dialog
        title={intl.formatMessage({ id: "billing.insufficient_funds" })}
        description={
          <>
            <p>
              {intl.formatMessage({ id: "billing.insufficient_funds.note" })}
            </p>
            <Fallback />
          </>
        }
        trigger={
          <Tag
            style={{
              backgroundColor: "var(--lemon-chiffon)",
              color: "var(--old-gold)"
            }}
          >
            {intl.formatMessage({ id: "billing.insufficient_funds" })}
          </Tag>
        }
      />
    ) : null;
  };

  return (
    <O.PanelWrapper>
      <O.Stack>
        <S.PanelRow>
          <Typography.BodyText weight="bold">
            {intl.formatMessage({ id: "payment_method" })}
          </Typography.BodyText>
          <Button
            variant="outline"
            to={`/${organization.name}/-/billing/details`}
            analyticId="edit"
          >
            {intl.formatMessage({ id: "billing_details" })}
          </Button>
        </S.PanelRow>
        <O.Stack space={8}>
          {prepayment?.balance && (
            <S.PanelRow>
              <O.SpacedRow>
                <Typography.Title tag="h2">
                  {prepayment.balance.amount > 0 && "+"}
                  {prepayment.balance.formatted}
                </Typography.Title>
                <InsufficientPayment />
              </O.SpacedRow>
            </S.PanelRow>
          )}
          <S.PanelRow>
            <O.SpacedRow>
              {paymentDetails.icon}
              <S.SmallText>{paymentDetails.left}</S.SmallText>
            </O.SpacedRow>
            {paymentDetails.right && (
              <S.SmallText>{paymentDetails.right}</S.SmallText>
            )}
          </S.PanelRow>
        </O.Stack>
      </O.Stack>
    </O.PanelWrapper>
  );
};

export default PaymentMethodPanel;
